import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import './ToolPage.css'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const formatBRL = (value) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const PricingCalculator = () => {
  useDocumentMeta({
    title: 'Calculadora de Precificação e Margem de Lucro | jarder.dev',
    description:
      'Calcule o preço de venda ideal a partir do custo e da margem de ' +
      'lucro desejada, ou descubra a margem real de um preço. Grátis.',
  })

  const [cost, setCost] = useState('50')
  const [margin, setMargin] = useState('30')
  const [expenses, setExpenses] = useState('0')

  const result = useMemo(() => {
    const costNum = parseFloat(cost.replace(',', '.'))
    const marginNum = parseFloat(margin.replace(',', '.'))
    const expensesNum = parseFloat(expenses.replace(',', '.')) || 0
    if (!costNum || marginNum === undefined || Number.isNaN(marginNum)) return null
    if (marginNum >= 100) return null
    const totalCost = costNum + expensesNum
    const price = totalCost / (1 - marginNum / 100)
    const profit = price - totalCost
    return { price, profit, totalCost }
  }, [cost, margin, expenses])

  const handleChange = () => trackEvent('tool_generate', { tool: 'pricing_calculator' })

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Calculadora de Precificação e Margem</h1>
      <p className="tool-disclaimer">
        Preço de venda = custo total ÷ (1 − margem desejada). Margem
        aqui é sobre o preço de venda (markup sobre o preço final), não
        sobre o custo.
      </p>

      <div className="tool-row">
        <div className="tool-field">
          <label htmlFor="cost">Custo do produto (R$)</label>
          <input
            id="cost"
            type="text"
            inputMode="decimal"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            onBlur={handleChange}
          />
        </div>
        <div className="tool-field">
          <label htmlFor="expenses">Outras despesas por unidade (R$)</label>
          <input
            id="expenses"
            type="text"
            inputMode="decimal"
            value={expenses}
            onChange={(e) => setExpenses(e.target.value)}
            onBlur={handleChange}
          />
        </div>
        <div className="tool-field">
          <label htmlFor="margin">Margem de lucro desejada (%)</label>
          <input
            id="margin"
            type="text"
            inputMode="decimal"
            value={margin}
            onChange={(e) => setMargin(e.target.value)}
            onBlur={handleChange}
          />
        </div>
      </div>

      {result && (
        <div className="tool-result-card">
          <span className="tool-result-label">Preço de venda sugerido</span>
          <span className="tool-result-value">{formatBRL(result.price)}</span>
          <ul className="tool-list">
            <li>
              <strong>Custo total:</strong> {formatBRL(result.totalCost)}
            </li>
            <li>
              <strong>Lucro por unidade:</strong> {formatBRL(result.profit)}
            </li>
          </ul>
        </div>
      )}

      <div className="tool-info-section">
        <h2>Margem sobre venda vs. markup sobre custo</h2>
        <p>
          Um erro comum é confundir os dois: se você quer <strong>30% de
          margem sobre o preço de venda</strong>, o cálculo é custo ÷
          (1  0,3)  não custo × 1,3, que na verdade dá uma margem menor
          que 30% (porque o markup incide sobre o custo, não sobre o
          preço final). Essa calculadora usa a primeira forma, mais
          usada em precificação de produtos e serviços.
        </p>
      </div>
    </div>
  )
}

export default PricingCalculator
